import React, { useEffect, useState, useCallback, useRef } from 'react';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft, faArrowUp, faArrowDown,
  faExclamationTriangle, faCheckCircle, faCoins, faRotate,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../components/shared/DataTable';
import { UiPageHeader, UiFilterPanel, UiStatsGrid, UiButton, UiSelect } from '../components/ui';
import type { StatCardDef } from '../components/ui';
import { fmtMoney } from '../utils/formatMoney';
import dayjs from 'dayjs';

const WalletTransactionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [dateFrom, setDateFrom] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [appliedDateTo, setAppliedDateTo] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [transactionType, setTransactionType] = useState('');
  const [appliedType, setAppliedType] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  const [summary, setSummary] = useState({
    credit_count: 0, credit_amount: 0,
    debit_count: 0, debit_amount: 0,
    total_fee: 0,
  });

  const abortRef = useRef<AbortController | null>(null);

  const loadTransactions = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      let url = `/merchants/wallet/transactions?limit=${pageSize}&offset=${offset}`;
      if (appliedType) url += `&transaction_type=${appliedType}`;
      if (appliedDateFrom) url += `&date_from=${appliedDateFrom}`;
      if (appliedDateTo) url += `&date_to=${appliedDateTo}`;

      const res = await merchantApi.get(url, { signal: controller.signal });
      if (!controller.signal.aborted) {
        setTransactions(res.data.data || []);
        setTotalItems(res.data.pagination?.total || 0);
        if (res.data.summary) setSummary(res.data.summary);
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError' && error?.code !== 'ERR_CANCELED') {
        toast.error(t('common.cannotLoadData'));
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [appliedType, currentPage, pageSize, appliedDateFrom, appliedDateTo, t]);

  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error(t('order.dateFromError'));
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setAppliedType(transactionType);
    setCurrentPage(1);
  };

  const handleReset = () => {
    const today = dayjs().format('YYYY-MM-DD');
    setDateFrom(today);
    setDateTo(today);
    setTransactionType('');
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
    setAppliedType('');
    setCurrentPage(1);
  };

  const netAmount = summary.credit_amount - summary.debit_amount;

  // const statCards: StatCardDef[] = [
  //   {
  //     icon: faArrowUp,
  //     iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
  //     label: t('walletTx.creditLabel'),
  //     value: <span className="text-green-600 dark:text-green-400">฿{fmtMoney(summary.credit_amount)}</span>,
  //     subValue: <span className="text-xs text-gray-400 dark:text-slate-500">{summary.credit_count.toLocaleString()} {t('walletTx.countSuffix')}</span>,
  //   },
  //   {
  //     icon: faArrowDown,
  //     iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  //     label: t('walletTx.debitLabel'),
  //     value: <span className="text-red-500 dark:text-red-400">฿{fmtMoney(summary.debit_amount)}</span>,
  //     subValue: <span className="text-xs text-gray-400 dark:text-slate-500">{summary.debit_count.toLocaleString()} {t('walletTx.countSuffix')}</span>,
  //   },
  //   {
  //     icon: faExclamationTriangle,
  //     iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  //     label: t('walletTx.totalFee'),
  //     value: <span className="text-amber-600 dark:text-amber-400">-฿{fmtMoney(summary.total_fee)}</span>,
  //   },
  //   {
  //     icon: netAmount >= 0 ? faCheckCircle : faArrowRightArrowLeft,
  //     iconClassName: netAmount >= 0
  //       ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
  //       : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  //     label: t('walletTx.netAmount'),
  //     value: (
  //       <span className={netAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
  //         {netAmount >= 0 ? '+' : '-'}฿{fmtMoney(Math.abs(netAmount))}
  //       </span>
  //     ),
  //   },
  // ];

  const columns: Column<any>[] = [
    {
      key: 'transaction_type',
      header: t('walletTx.colType'),
      sortable: true,
      render: (tx) =>
        tx.transaction_type === 'credit' ? (
          <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-semibold inline-flex items-center gap-1.5">
            <FontAwesomeIcon icon={faArrowUp} /> {t('walletTx.credit')}
          </span>
        ) : (
          <span className="px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full text-xs font-semibold inline-flex items-center gap-1.5">
            <FontAwesomeIcon icon={faArrowDown} /> {t('walletTx.debit')}
          </span>
        ),
    },
    {
      key: 'amount',
      header: t('walletTx.colAmount'),
      sortable: true,
      render: (tx) => (
        <span className={`font-bold text-sm ${tx.transaction_type === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
          {tx.transaction_type === 'credit' ? '+' : '-'}฿{fmtMoney(tx.amount)}
        </span>
      ),
    },
    {
      key: 'fee_amount',
      header: t('walletTx.colFee'),
      render: (tx) =>
        parseFloat(tx.fee_amount) > 0 ? (
          <span className="text-amber-600 dark:text-amber-400 text-sm font-semibold">-฿{fmtMoney(tx.fee_amount)}</span>
        ) : (
          <span className="text-gray-300 dark:text-slate-600">-</span>
        ),
    },
    {
      key: 'balance_after',
      header: t('walletTx.colBalanceAfter'),
      render: (tx) => {
        if (tx.balance_after === null || tx.balance_after === undefined) {
          return <span className="text-gray-300 dark:text-slate-600">-</span>;
        }
        const isPayoutTx = tx.description?.includes('หักจาก Payout') || tx.description?.includes('หักเงิน Payout') || tx.description?.includes('เติมเงินเข้า Payout Wallet');
        return (
          <div>
            <span className="font-semibold text-gray-700 dark:text-slate-200 text-sm">฿{fmtMoney(tx.balance_after)}</span>
            <p className={`text-[10px] mt-0.5 font-medium ${isPayoutTx ? 'text-purple-500 dark:text-purple-400' : 'text-blue-500 dark:text-blue-400'}`}>
              {isPayoutTx ? t('walletTx.payoutWallet') : t('walletTx.mainWallet')}
            </p>
          </div>
        );
      },
    },
    {
      key: 'description',
      header: t('walletTx.colDescription'),
      render: (tx) => (
        <div>
          <p className="text-gray-700 dark:text-slate-200 text-sm">{tx.description || '-'}</p>
          {tx.orders?.order_number && (
            <p className="text-xs text-blue-500 dark:text-blue-400 mt-0.5">Order: {tx.orders.order_number}</p>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: t('walletTx.colDate'),
      sortable: true,
      render: (tx) => (
        <div>
          <p className="text-sm text-gray-700 dark:text-slate-200">{dayjs(tx.created_at).format('DD/MM/YYYY')}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">{dayjs(tx.created_at).format('HH:mm:ss')}</p>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faCoins}
        title={t('walletTx.title')}
        subtitle={`${t('walletTx.subtitle')} • ${totalItems.toLocaleString()} ${t('walletTx.countSuffix')}`}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <UiButton variant="secondary" icon={faRotate} onClick={() => loadTransactions()}>
            {t('common.refresh')}
          </UiButton>
        }
      >
        <UiSelect
          wrapperClassName="flex-1 min-w-[160px]"
          label={t('walletTx.filterType')}
          value={transactionType}
          onChange={(e) => setTransactionType(e.target.value)}
        >
          <option value="">{t('walletTx.allTypes')}</option>
          <option value="credit">{t('walletTx.creditLabel')}</option>
          <option value="debit">{t('walletTx.debitLabel')}</option>
        </UiSelect>
      </UiFilterPanel>

      {/* <UiStatsGrid cards={statCards} loading={loading} cols={4} /> */}

      <DataTable
        data={transactions}
        columns={columns}
        loading={loading}
        emptyMessage={t('walletTx.noTransactions')}
        emptyIcon={<FontAwesomeIcon icon={faCoins} className="text-5xl text-gray-200" />}
        serverPagination={{
          currentPage,
          totalItems,
          pageSize,
          onPageChange: (page) => setCurrentPage(page),
          onPageSizeChange: (size) => { setPageSize(size); setCurrentPage(1); },
        }}
        hoverable
      />
    </div>
  );
};

export default WalletTransactionsPage;
